import { useCallback, useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "../FormField";
import { fmtDate } from "../../helpers";
import { Plug, ExternalLink, RefreshCw, Settings2, CheckCircle2, XCircle } from "lucide-react";

export default function IntegrationsTab({ apartmentId }) {
  const [catalog, setCatalog] = useState([]);
  const [saved, setSaved] = useState([]);
  const [cfg, setCfg] = useState(null);   // { provider entry + form }

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        api.get(`/integrations/catalog`),
        api.get(`/integrations`, { params: { apartment_id: apartmentId } }),
      ]);
      setCatalog(c.data); setSaved(s.data);
    } catch { /* noop */ }
  }, [apartmentId]);
  useEffect(() => { load(); }, [load]);

  const byProvider = (p) => saved.find((s) => s.provider === p && s.apartment_id === apartmentId);

  const openCfg = (entry) => {
    const ex = byProvider(entry.key) || {};
    setCfg({ entry, deep_link: ex.deep_link || "", external_id: ex.external_id || "", enabled: ex.enabled ?? true, config: { ...(ex.config || {}) } });
  };
  const saveCfg = async () => {
    try {
      await api.post(`/integrations`, {
        apartment_id: apartmentId, provider: cfg.entry.key,
        deep_link: cfg.deep_link, external_id: cfg.external_id, enabled: cfg.enabled, config: cfg.config,
      });
      toast.success("Integrazione salvata"); setCfg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const sync = async (ig) => {
    if (!ig) return toast.error("Configura prima l'integrazione");
    try { const r = await api.post(`/integrations/${ig.id}/sync`); toast.message(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <Card className="border-slate-200 p-5" data-testid="detail-integrations">
      <h3 className="mb-1 flex items-center gap-2 font-head font-semibold text-slate-900"><Plug size={18} /> Hub Integrazioni</h3>
      <p className="mb-4 text-xs text-slate-400">Configura ora collegamenti, ID e credenziali. La sincronizzazione reale verrà abilitata progressivamente con le API.</p>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {catalog.map((entry) => {
          const ig = byProvider(entry.key);
          const connected = ig?.status === "connesso" && ig?.enabled;
          return (
            <div key={entry.key} data-testid={`integ-${entry.key}`} className="rounded-lg border border-slate-200 p-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="font-medium text-slate-800">{entry.label}</span>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {connected ? <CheckCircle2 size={11} /> : <XCircle size={11} />}{connected ? "Connesso" : "Non connesso"}
                </span>
              </div>
              <div className="mb-2 text-[11px] text-slate-400">{entry.category}
                {ig?.last_sync && <> · sync {fmtDate(ig.last_sync)}</>}
                {ig?.last_error && <span className="text-rose-500"> · {ig.last_error}</span>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" data-testid={`integ-config-${entry.key}`} onClick={() => openCfg(entry)}><Settings2 size={13} className="mr-1" />Configura</Button>
                {ig?.deep_link && <Button size="sm" variant="outline" data-testid={`integ-open-${entry.key}`} onClick={() => window.open(ig.deep_link, "_blank")}><ExternalLink size={13} /></Button>}
                {entry.sync && <Button size="sm" variant="ghost" data-testid={`integ-sync-${entry.key}`} onClick={() => sync(ig)}><RefreshCw size={13} /></Button>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!cfg} onOpenChange={(o) => !o && setCfg(null)}>
        <DialogContent data-testid="integ-config-dialog">
          <DialogHeader><DialogTitle>Configura · {cfg?.entry?.label}</DialogTitle></DialogHeader>
          {cfg && (
            <div className="space-y-3">
              <Field label="Deep-link / URL"><Input data-testid="integ-deeplink" value={cfg.deep_link} onChange={(e) => setCfg((c) => ({ ...c, deep_link: e.target.value }))} placeholder="https://..." /></Field>
              <Field label="ID esterno"><Input value={cfg.external_id} onChange={(e) => setCfg((c) => ({ ...c, external_id: e.target.value }))} /></Field>
              {(cfg.entry.fields || []).map((fld) => (
                <Field key={fld} label={fld}><Input value={cfg.config[fld] || ""} onChange={(e) => setCfg((c) => ({ ...c, config: { ...c.config, [fld]: e.target.value } }))} /></Field>
              ))}
              <div className="flex items-center justify-between rounded-lg bg-secondary/40 px-3 py-2">
                <span className="text-sm text-slate-600">Abilitata</span>
                <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} data-testid="integ-enabled" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCfg(null)}>Annulla</Button>
                <Button className="bg-primary hover:bg-primary/90" data-testid="integ-save" onClick={saveCfg}>Salva</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
